/**
 * hero-banner.js
 *
 * Controller for the Hero Banner section.
 * Handles video play/pause & volume, countdown timer, and CTA stacking.
 * Loaded via <script defer> from hero-banner.liquid.
 */
(function () {
  'use strict';

  /* -------------------------------------------------------
   * Video controls — play/pause & volume toggles
   * (Task 4.2)
   * ----------------------------------------------------- */
  /**
   * Initialise video play/pause and volume controls for a Hero Banner section.
   * Attaches click handlers that toggle playback/mute state and swap SVG icons
   * with matching aria-labels for accessibility.
   * @param {HTMLElement} sectionEl — the root <section> element
   */
  function initVideoControls(sectionEl) {
    const video = sectionEl.querySelector('.hero-banner__video');
    if (!video) return;

    const playPauseBtn = sectionEl.querySelector('.hero-banner__play-pause');
    const volumeBtn = sectionEl.querySelector('.hero-banner__volume');
    const sectionId = sectionEl.id.replace('HeroBanner-', '');

    /* — SVG icons ---------------------------------------------------- */
    const ICON_PAUSE = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#45423D" width="20" height="20"><path stroke-linecap="round" stroke-linejoin="round" d="M14.25 9v6m-4.5 0V9M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>';
    const ICON_PLAY = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#45423D" width="20" height="20"><path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM15.91 11.672a.375.375 0 0 1 0 .656l-5.603 3.113a.375.375 0 0 1-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112Z" /></svg>';
    const ICON_VOL_OFF = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#45423D" width="20" height="20"><path stroke-linecap="round" stroke-linejoin="round" d="M17.25 9.75 19.5 12m0 0 2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6 4.72-3.72a.75.75 0 0 1 1.28.53v14.88a.75.75 0 0 1-1.28.53l-4.72-3.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" /></svg>';
    const ICON_VOL_ON = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#45423D" width="20" height="20"><path stroke-linecap="round" stroke-linejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-3.72a.75.75 0 0 1 1.28.53v14.88a.75.75 0 0 1-1.28.53l-4.72-3.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" /></svg>';

    /* — Play / Pause ------------------------------------------------- */
    const togglePlayPause = () => {
      const iconEl = document.getElementById('HeroBannerPlayPauseIcon-' + sectionId);
      if (!iconEl) return;

      if (video.paused) {
        video.play();
        iconEl.innerHTML = ICON_PAUSE;
        playPauseBtn.setAttribute('aria-label', 'Pausa video');
      } else {
        video.pause();
        iconEl.innerHTML = ICON_PLAY;
        playPauseBtn.setAttribute('aria-label', 'Riproduci video');
      }
    };

    /* — Volume -------------------------------------------------------- */
    const toggleVolume = () => {
      const iconEl = document.getElementById('HeroBannerVolumeIcon-' + sectionId);
      if (!iconEl) return;

      if (video.muted) {
        video.muted = false;
        iconEl.innerHTML = ICON_VOL_ON;
        volumeBtn.setAttribute('aria-label', 'Disattiva audio');
      } else {
        video.muted = true;
        iconEl.innerHTML = ICON_VOL_OFF;
        volumeBtn.setAttribute('aria-label', 'Attiva audio');
      }
    };

    /* — Bind events --------------------------------------------------- */
    if (playPauseBtn) {
      playPauseBtn.addEventListener('click', togglePlayPause);
    }

    if (volumeBtn) {
      volumeBtn.addEventListener('click', toggleVolume);
    }
  }

  /* -------------------------------------------------------
   * Countdown timer — date-based visibility & live update
   * (Task 4.3)
   * ----------------------------------------------------- */

  /**
   * Check whether the current date falls between start and end.
   * @param {Date} start — promotion start date
   * @param {Date} end   — promotion end date
   * @returns {boolean} true if now is within the promotional window
   */
  function checkVisibility(start, end) {
    const now = new Date();
    return now >= start && now <= end;
  }

  /**
   * Initialise the promotional countdown timer for a Hero Banner section.
   * Reads date boundaries from data attributes, builds the digit/label HTML,
   * and ticks every second until the promotion ends (then auto-hides).
   * @param {HTMLElement} sectionEl — the root <section> element
   */
  function initCountdown(sectionEl) {
    const container = sectionEl.querySelector('.hero-banner__countdown');
    if (!container) return;

    const startStr = sectionEl.dataset.countdownStart;
    const endStr = sectionEl.dataset.countdownEnd;
    const timezone = sectionEl.dataset.countdownTimezone || 'UTC';

    if (!startStr || !endStr) return;

    /* — Parse dates, appending timezone when the string has no offset --- */
    const parseDate = (str) => {
      // If the string already contains a Z, +, or trailing offset, use as-is
      if (/[Zz]/.test(str) || /[+-]\d{2}:\d{2}$/.test(str)) {
        return new Date(str);
      }
      // Otherwise, build an Intl-based offset for the configured timezone
      try {
        const probe = new Date(str + 'Z'); // treat as UTC temporarily
        const utcStr = probe.toLocaleString('en-US', { timeZone: 'UTC' });
        const tzStr = probe.toLocaleString('en-US', { timeZone: timezone });
        const diffMs = new Date(utcStr) - new Date(tzStr);
        return new Date(probe.getTime() + diffMs);
      } catch (_e) {
        return new Date(str);
      }
    };

    const start = parseDate(startStr);
    const end = parseDate(endStr);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return;

    /* — Visibility gate ------------------------------------------------ */
    if (!checkVisibility(start, end)) {
      container.classList.add('tw-hidden');
      return;
    }

    /* — Build countdown HTML ------------------------------------------- */
    const units = [
      { key: 'days', suffix: 'D' },
      { key: 'hours', suffix: 'H' },
      { key: 'minutes', suffix: 'M' },
      { key: 'seconds', suffix: 'S' },
    ];

    container.innerHTML = units
      .map((u, i) => {
        const sep = i < units.length - 1
          ? '<span class="hero-banner__countdown-sep">\u2005:\u2005</span>'
          : '';
        return (
          '<span class="hero-banner__countdown-unit">' +
            '<span class="hero-banner__countdown-digits" data-unit="' + u.key + '">00</span>' +
            '<span class="hero-banner__countdown-suffix">' + u.suffix + '</span>' +
          '</span>' + sep
        );
      })
      .join('');

    /* — Digit elements cache ------------------------------------------ */
    const digitEls = {
      days: container.querySelector('[data-unit="days"]'),
      hours: container.querySelector('[data-unit="hours"]'),
      minutes: container.querySelector('[data-unit="minutes"]'),
      seconds: container.querySelector('[data-unit="seconds"]'),
    };

    const pad = (n) => String(n).padStart(2, '0');

    let intervalId = null;

    /** Hide countdown and clear the interval. */
    const hideOnZero = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      container.classList.add('tw-hidden');
    };

    /** Recalculate remaining time and update digit spans. */
    const updateCountdown = () => {
      const remaining = end - new Date();

      if (remaining <= 0) {
        hideOnZero();
        return;
      }

      const totalSec = Math.floor(remaining / 1000);
      const days = Math.floor(totalSec / 86400);
      const hours = Math.floor((totalSec % 86400) / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;

      digitEls.days.textContent = pad(days);
      digitEls.hours.textContent = pad(hours);
      digitEls.minutes.textContent = pad(minutes);
      digitEls.seconds.textContent = pad(seconds);
    };

    /* — Kick off ------------------------------------------------------- */
    updateCountdown();
    intervalId = setInterval(updateCountdown, 1000);

    /* Show the container (it starts with tw-hidden in Liquid) */
    container.classList.remove('tw-hidden');
  }

  /* -------------------------------------------------------
   * CTA stacking — ResizeObserver line-wrap detection
   * (Task 4.4)
   * ----------------------------------------------------- */
  /**
   * Responsive CTA stacking — uses a ResizeObserver to detect when a CTA
   * button's text wraps to a second line on narrow viewports. When wrapping
   * is detected the container receives a `hero-banner__ctas--stacked` class
   * that switches the layout from side-by-side to full-width vertical stack.
   *
   * @param {HTMLElement} sectionEl — the root <section> element
   */
  function initCtaStacking(sectionEl) {
    const container = sectionEl.querySelector('.hero-banner__ctas');
    if (!container) return;

    const ctas = container.querySelectorAll('.hero-banner__cta');
    if (ctas.length < 2) return;

    /**
     * Compute a single-line height threshold for a CTA button.
     * A button whose offsetHeight exceeds this value has wrapped text.
     * Threshold = lineHeight * 1.5 + vertical padding.
     * @param {HTMLElement} cta
     * @returns {number}
     */
    const getThreshold = (cta) => {
      const style = getComputedStyle(cta);
      const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
      const paddingTop = parseFloat(style.paddingTop) || 0;
      const paddingBottom = parseFloat(style.paddingBottom) || 0;
      return lineHeight * 1.5 + paddingTop + paddingBottom;
    };

    /** Check all CTAs and toggle the stacked class on the container. */
    const checkStacking = () => {
      let shouldStack = false;

      ctas.forEach((cta) => {
        const threshold = getThreshold(cta);
        if (cta.scrollHeight > threshold) {
          shouldStack = true;
        }
      });

      container.classList.toggle('hero-banner__ctas--stacked', shouldStack);
    };

    const observer = new ResizeObserver(checkStacking);
    ctas.forEach((cta) => observer.observe(cta));

    // Run an initial check
    checkStacking();

    // Store observer reference on the section for cleanup on unload
    sectionEl._heroBannerCtaObserver = observer;
  }

  /* -------------------------------------------------------
   * Main entry point — initialises all sub-controllers
   * for a single Hero Banner section element.
   * ----------------------------------------------------- */
  function initHeroBanner(sectionEl) {
    if (!sectionEl) return;
    initVideoControls(sectionEl);
    initCountdown(sectionEl);
    initCtaStacking(sectionEl);
  }

  /* -------------------------------------------------------
   * Bootstrap — find every Hero Banner on the page
   * ----------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[id^="HeroBanner-"]').forEach(function (section) {
      initHeroBanner(section);
    });
  });

  /* -------------------------------------------------------
   * Theme editor support — initialise newly loaded sections
   * ----------------------------------------------------- */
  document.addEventListener('shopify:section:load', function (evt) {
    var section = evt.target.querySelector('[id^="HeroBanner-"]');
    initHeroBanner(section);
  });

  /* -------------------------------------------------------
   * Theme editor cleanup — disconnect observers on unload
   * ----------------------------------------------------- */
  document.addEventListener('shopify:section:unload', function (evt) {
    var section = evt.target.querySelector('[id^="HeroBanner-"]');
    if (section && section._heroBannerCtaObserver) {
      section._heroBannerCtaObserver.disconnect();
      section._heroBannerCtaObserver = null;
    }
  });
})();
